import {
  createStyles,
  Theme,
  makeStyles
} from '@material-ui/core';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { isMobile } from 'react-device-detect';
/* import components */
import CustomFade from '../../helpers/CustomFade';

import {
  shortCutDocumentTop,
  useImagePreviewKeyboardShortCuts
} from '../ImagePreview/hooks/useImagePreviewKeyboardShortCuts';
import { makeTip } from '../ImagePreview/ImagePreviewControls';
import { PanZoomContext } from '../ImagePreview/PanZoomContext';
import Button from '@cube3/cubicle/src/core/atoms/Button/Button';
import { Tooltip } from '@cube3/cubicle/src/core/atoms/Tooltip/Tooltip';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography/Typography';
import { ZoomButtons } from '../ImagePreview/ZoomButtons';
import { FullscreenButton } from '../VideoPreview/FullscreenButton';
import { videoPlayerShortCutsTips } from '../VideoPreview/hooks/useVideoPlayerKeyboardShortcuts';
import { useCurrentShareLink } from '../../../../state/src/redux/components/Hooks/useCurrentShareLink';
import { AddCommentButton } from '../VideoPreview/AddCommentButton';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import { ContextMenu } from '@cube3/cubicle/src/core/molecules/ContextMenu';
import { ContextMenuListItem } from '@cube3/cubicle/src/core/molecules/ContextMenu/ContextMenuListItem';
import useOutsideClick from '@cube3/cubicle/src/helpers/hooks/UseOutsideClick';


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mobile: {},
    container: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-around',
      alignItems: 'center',
      flexWrap: 'wrap',
      paddingBottom: theme.customSpacing.padding[20],
      paddingLeft: theme.customSpacing.padding[24],
      paddingRight: theme.customSpacing.padding[24],
      paddingTop: theme.customSpacing.padding[32],
      '-webkit-tap-highlight-color': 'transparent', //remove blue highlight from chrome
      '&$mobile': {
        padding: theme.customSpacing.padding[8],
        position: 'absolute',
        width: 'calc(100% - 16px)',
        bottom: 0
      }
    },
    barGroup: {
      display: 'flex',
      flexFlow: 'row nowrap',
      justifyContent: 'space-between',
      alignItems: 'center',
      textAlign: 'center',
      width: '100%',
      '&$mobile': {
        justiftContent: 'center'
      }
    },
    leftNavControls: {
      display: 'flex',
      alignSelf: 'flex-start',
      width: theme.sizing[92],
      justifyContent: 'space-between',
      '$mobile &': {
        width: 'auto'
      }
    },
    rightControls: {
      display: 'flex',
      '$mobile &': {
        width: '100px',
        justifyContent: 'flex-end',
        aligntItems: 'center'
      }
    },
    zoomInIcon: {
      margin: `0px ${theme.customSpacing.margin[24]} 0px ${theme.customSpacing.margin[12]}`,
      '$mobile &': {
        margin: 'auto'
      }
    },
    inputContainer: {
      justifySelf: 'center',
      '$mobile &': {
        display: 'flex',
        justifyContent: 'center',
        flexGrow: 1,
        paddingLeft: '40px'
      }
    },
    totalPages: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      marginLeft: theme.customSpacing.margin[16],
      '$mobile &': {
        height: 'auto'
      }
    },
    divider: {
      display: 'flex',
      alignItems: 'center'
    },
    sameline: {
      display: 'flex',
      alignItems: 'center',
    },
    arrowUp: {
      marginRight: '12px',
      '$mobile &': {
        marginRight: 0,
      }
    },
    scrim: {
      pointerEvents: 'none',
      position: 'absolute',
      height: '56px',
      left: 0,
      bottom: 0,
      right: 0,
      background: theme.customPalette.surfaceAccent.onBase.scrim3
    }
  })
);

interface DocumentPreviewControlsProps {
  toggleFullscreenAction: () => void;
  // zoomOut: () => void;
  // zoomIn: () => void;
  next?: () => void;
  previous?: () => void;
  goToPage?: (ev: React.SyntheticEvent | number) => void;
  // zoom: number;
  //minZoom: number;
  //maxZoom: number;
  current: number;
  totalPages: number;
  fullscreen: boolean;
  renderFullscreenControls?: () => React.ReactNode;
  mainContainerRef: HTMLDivElement;
}

const DocumentPreviewControls: React.FC<DocumentPreviewControlsProps> = (
  props
) => {
  const {
    toggleFullscreenAction,
    current,
    totalPages,
    goToPage,
    fullscreen,
    renderFullscreenControls
  } = props;

  const { zoomIn, zoomOut, canZoomOut, canZoomIn, zoomReset, canFitZoom } =
    React.useContext(PanZoomContext);

  const handleScrollTopClick = useCallback(() => {
    goToPage(1);
  }, [goToPage]);

  useImagePreviewKeyboardShortCuts({
    zoomIn,
    zoomOut,
    zoomReset,
    canZoomIn,
    canZoomOut,
    canFitZoom,
    toggleFullscreenAction,
    canGoToPage: current !== 1,
    goToPage: handleScrollTopClick
  });

  const pages = new Array(totalPages).fill(null);

  //if review or share show comment button
  const typeOfView = useCurrentShareLink()?.type;
  const useComment = typeOfView === 'share';


  const classes = useStyles();

  const [pickPage, setPickPage] = useState(false);

  const handlePickPage = useCallback(() => {
    setPickPage(!pickPage);
  }, [pickPage]);

  const anchorRef = React.useRef();
  const outsideRef = useOutsideClick(() => setPickPage(false));


  return (
    <div className={`${classes.container} ${isMobile ? classes.mobile : ''}`}>
      {isMobile && <div className={classes.scrim} />}
      {fullscreen && renderFullscreenControls()}
      <div className={`${classes.barGroup} ${isMobile ? classes.mobile : ''}`} ref={outsideRef}>
        <div className={classes.leftNavControls}></div>
        <div className={classes.inputContainer}>
          <div className={classes.sameline} ref={anchorRef} >
            <span className={isMobile && classes.arrowUp}>
              <Tooltip content={isMobile ? '' : makeTip(shortCutDocumentTop)} placement="bottom">
                <Button
                  aria-label="scroll to top"
                  iconLeft="arrow_up"
                  background="secondary"
                  buttonStyle="ghost"
                  disabled={current === 1}
                  onClick={handleScrollTopClick}
                  size={isMobile ? SizeVariants.lg : SizeVariants.md}
                />
              </Tooltip>
            </span>
            <span className={classes.sameline} style={{ marginRight: 4 }}>
              <Button
                aria-label="current page"
                label={current.toString()}
                background="secondary"
                buttonStyle="ghost"
                onClick={handlePickPage}
                size={isMobile ? SizeVariants.lg : SizeVariants.md}
              />
              {pickPage &&
                <ContextMenu width={16} anchor={anchorRef.current} placement='top-end' disableFloPortal={fullscreen}>
                  {pages.map((p, idx) => {
                    return (
                      <ContextMenuListItem key={(idx + 1).toString()} value={(idx + 1).toString()} showChevron={false} onClick={() => { goToPage(idx + 1); handlePickPage(); }}
                        extraRootProps={{ 'data-pw': 'page' }}
                      />
                    );
                  })}

                </ContextMenu>
              }
            </span>
            <span className={classes.divider}>
              <Typography>/</Typography>
            </span>
            <div className={classes.totalPages}>
              <Typography colorVariant="02" variant={'md'}>{totalPages}</Typography>
            </div>
          </div>
        </div>
        <div className={classes.rightControls}>
          {!isMobile && <ZoomButtons />}

          {isMobile && useComment &&
            <AddCommentButton />
          }

          <FullscreenButton
            fullscreen={fullscreen}
            toggleFullscreenAction={toggleFullscreenAction}
            shortCutTip={videoPlayerShortCutsTips.fullscreen}
          />

        </div>
      </div>
    </div>
  );
};


export default isMobile ? DocumentPreviewControls : CustomFade(DocumentPreviewControls);